import type { LocationQuery, LocationQueryRaw } from "vue-router";

import type { MerchantApiClient } from "../../types.js";
import { compactQuery, readNumberQuery, readStringQuery } from "../../shared/view-helpers.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createAuditApi, type AuditApi } from "./api.js";
import {
  actionLabel,
  actorTypeLabel,
  auditActorLabel,
  auditTimestampLabel,
  entityTypeLabel,
  safeSummary,
  toAuditRow,
} from "./formatters.js";
import {
  AUDIT_ACTION_GROUPS,
  auditActorOptions,
  auditEntityOptions,
  defaultAuditFilters,
  summaryRows,
  toAuditFilters,
  type AuditDetailViewModel,
  type AuditEntry,
  type AuditFilters,
  type AuditFiltersViewModel,
  type AuditPage,
  type AuditPageViewModel,
} from "./models.js";

export interface AuditTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface AuditService {
  filtersFromRouteQuery(query: LocationQuery): AuditFiltersViewModel;
  loadAudit(
    filters: AuditFilters,
    draftFilters?: AuditFiltersViewModel,
  ): Promise<AuditPageViewModel>;
  loadAuditEntry(auditId: string): Promise<AuditDetailViewModel>;
  routeQueryFromFilters(filters: AuditFiltersViewModel, selectedAuditId?: string): LocationQueryRaw;
}

export interface AuditServiceOptions {
  api?: AuditApi;
  apiClient?: MerchantApiClient;
  telemetry?: AuditTelemetry;
}

export function createAuditService(options: AuditServiceOptions): AuditService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();

  return {
    filtersFromRouteQuery(query) {
      return filtersFromRouteQuery(query);
    },
    async loadAudit(filters, draftFilters) {
      const page = await track(telemetry, "merchant_audit_loaded", () => api.listAudit(filters), {
        actionGroup: filters.actionGroup || undefined,
        actorType: filters.actorType,
      });
      return toAuditPageViewModel(page, draftFilters ?? toFilterViewModel(filters));
    },
    async loadAuditEntry(auditId) {
      const entry = await track(
        telemetry,
        "merchant_audit_entry_loaded",
        () => api.getAuditEntry(auditId),
        { auditId },
      );
      return toAuditDetailViewModel(entry);
    },
    routeQueryFromFilters(filters, selectedAuditId) {
      return routeQueryFromFilters(filters, selectedAuditId);
    },
  };
}

function filtersFromRouteQuery(query: LocationQuery): AuditFiltersViewModel {
  const defaults = defaultAuditFilters();
  const cursor = readStringQuery(query, "cursor");
  return {
    search: readStringQuery(query, "q") ?? defaults.search,
    dateWindow: readEnum(
      readStringQuery(query, "dateWindow"),
      ["LAST_7_DAYS", "LAST_15_DAYS", "LAST_30_DAYS", "CUSTOM"],
      defaults.dateWindow,
    ),
    actorType: readEnum(
      readStringQuery(query, "actorType"),
      auditActorOptions.map((option) => option.value),
      defaults.actorType,
    ),
    actionGroup: readEnum(
      readStringQuery(query, "actionGroup"),
      ["", ...AUDIT_ACTION_GROUPS],
      defaults.actionGroup,
    ),
    entityType: readEnum(
      readStringQuery(query, "entityType"),
      auditEntityOptions.map((option) => option.value),
      defaults.entityType,
    ),
    from: validDateString(readStringQuery(query, "from")) ?? defaults.from,
    to: validDateString(readStringQuery(query, "to")) ?? defaults.to,
    limit: normalizeLimit(readNumberQuery(query, "limit")),
    ...(cursor === undefined ? {} : { cursor }),
  };
}

function routeQueryFromFilters(
  filters: AuditFiltersViewModel,
  selectedAuditId?: string,
): LocationQueryRaw {
  return compactQuery({
    q: filters.search.trim(),
    dateWindow: filters.dateWindow === "LAST_30_DAYS" ? undefined : filters.dateWindow,
    actorType: filters.actorType,
    actionGroup: filters.actionGroup,
    entityType: filters.entityType,
    from: filters.dateWindow === "CUSTOM" ? filters.from : undefined,
    to: filters.dateWindow === "CUSTOM" ? filters.to : undefined,
    limit: filters.limit === 25 ? undefined : filters.limit,
    cursor: filters.cursor,
    auditId: selectedAuditId,
  });
}

function toAuditPageViewModel(page: AuditPage, filters: AuditFiltersViewModel): AuditPageViewModel {
  return {
    appliedFilters: filters,
    draftFilters: filters,
    rows: page.entries.map(toAuditRow),
    pagination: {
      nextCursor: page.nextCursor,
      canGoNext: page.nextCursor !== null,
      staleCursor: false,
    },
    sectionStates:
      page.entries.length === 0
        ? [{ id: "table", status: "EMPTY", message: "No audit entries match these filters." }]
        : [],
    ...(page.entries[0] === undefined
      ? {}
      : { selectedEntry: toAuditDetailViewModel(page.entries[0]) }),
    ...(page.requestId === undefined ? {} : { requestId: page.requestId }),
  };
}

export function toAuditDetailViewModel(entry: AuditEntry): AuditDetailViewModel {
  return {
    id: entry.id,
    actorLabel: auditActorLabel(entry.actor),
    actorTypeLabel: actorTypeLabel(entry.actor.actorType),
    actionLabel: entry.actionLabel ?? actionLabel(entry.action),
    entityLabel:
      entry.targetEntityLabel ??
      `${entityTypeLabel(entry.targetEntityType)} ${entry.targetEntityId}`,
    timestampLabel: auditTimestampLabel(entry),
    ...(entry.reason === undefined ? {} : { reasonLabel: entry.reason }),
    ...(entry.message === undefined ? {} : { messageLabel: entry.message }),
    ...(entry.correlationId === undefined ? {} : { correlationId: entry.correlationId }),
    ...(entry.supportIndicator === undefined ? {} : { supportIndicator: entry.supportIndicator }),
    beforeRows: summaryRows(entry.beforeSummary),
    afterRows: summaryRows(entry.afterSummary),
  };
}

function toFilterViewModel(filters: AuditFilters): AuditFiltersViewModel {
  return {
    ...defaultAuditFilters(),
    search: filters.search ?? "",
    dateWindow: filters.dateWindow ?? "LAST_30_DAYS",
    actorType: filters.actorType ?? "",
    actionGroup: filters.actionGroup ?? "",
    entityType: filters.entityType ?? "",
    from: filters.from ?? "",
    to: filters.to ?? "",
    limit: filters.limit ?? 25,
    ...(filters.cursor === undefined ? {} : { cursor: filters.cursor }),
  };
}

export { defaultAuditFilters, toAuditFilters };

async function track<T>(
  telemetry: AuditTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): AuditApi {
  if (client === undefined) throw new Error("Audit service requires an API client.");
  return createAuditApi(client);
}

function readEnum<TValue extends string>(
  value: string | undefined,
  allowed: readonly TValue[],
  fallback: TValue,
): TValue {
  return value !== undefined && allowed.includes(value as TValue) ? (value as TValue) : fallback;
}

function normalizeLimit(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value) || value <= 0) return 25;
  return Math.min(Math.floor(value), 100);
}

function validDateString(value: string | undefined): string | undefined {
  if (value === undefined || value.trim().length === 0) return undefined;
  return Number.isNaN(new Date(value).getTime()) ? undefined : value;
}

function browserTelemetry(): AuditTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
